/**
 * Midday Interrupt - Anti-Scroll Mechanism
 * 
 * When they feel the urge to scroll, they tap "Enter Presence"
 * This becomes the replacement for unconscious scrolling
 * 
 * Micro-Immersion (60-90 seconds):
 * - Breath
 * - "Pause..."
 * - "You're here again"
 * - Quick body awareness
 * - Reset nervous system
 * 
 * Ends with Closing Anchor: Hand on heart, "I am here"
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Midday Interrupt - Anti-Scroll Reset (75 seconds total)
const MIDDAY_CONTENT = {
  totalDuration: 75000, // 75 seconds
  phases: [
    // Opening - 15 seconds
    {
      id: 'pause',
      startTime: 0,
      duration: 15000,
      text: "Pause",
      voiceText: null,
      breath: null,
    },
    // Breath - 15 seconds
    {
      id: 'breath-in',
      startTime: 15000,
      duration: 8000,
      text: null,
      voiceText: "Breathe in slowly...",
      breath: 'inhale',
    },
    {
      id: 'breath-out',
      startTime: 23000,
      duration: 7000,
      text: null,
      voiceText: "Release...",
      breath: 'exhale',
    },
    // Arrival - 15 seconds
    {
      id: 'here',
      startTime: 30000,
      duration: 15000,
      text: "You're here again",
      voiceText: null,
      breath: null,
    },
    // Body Awareness - 15 seconds
    {
      id: 'body',
      startTime: 45000,
      duration: 15000,
      text: null,
      voiceText: "Feel your feet on the ground... your hands... your breath...",
      breath: 'inhale',
    },
    // Reset - 10 seconds
    {
      id: 'reset',
      startTime: 60000,
      duration: 10000,
      text: "You don't need to escape",
      voiceText: null,
      breath: 'exhale',
    },
    // Closing Anchor - 5 seconds
    {
      id: 'anchor',
      startTime: 70000,
      duration: 5000,
      text: "I am here",
      voiceText: null,
      breath: null,
      isAnchor: true,
      isFinal: true,
    },
  ],
};

// Text Overlay Component
const TextOverlay = ({ text, isVisible, isAnchor }) => {
  return (
    <AnimatePresence mode="wait">
      {isVisible && text && (
        <motion.div
          key={text}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="absolute inset-0 flex items-center justify-center px-10 z-10"
        >
          <h2 
            className={`font-['Playfair_Display'] text-center leading-relaxed ${
              isAnchor 
                ? 'text-4xl text-white' 
                : 'text-4xl text-white/90'
            }`}
            style={{ textShadow: '0 0 60px rgba(74,103,65,0.4)' }}
          >
            {text}
          </h2>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Voice Text Component
const VoiceText = ({ text, isVisible }) => {
  return (
    <AnimatePresence mode="wait">
      {isVisible && text && (
        <motion.div
          key={text}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="absolute inset-x-0 bottom-1/3 text-center px-8 z-10"
        >
          <p 
            className="text-white/50 text-lg font-light italic"
            style={{ textShadow: '0 0 20px rgba(0,0,0,0.5)' }}
          >
            {text}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Breath Circle Component
const BreathCircle = ({ breath, isVisible }) => {
  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center pointer-events-none z-0"
    >
      <motion.div
        animate={{
          scale: breath === 'inhale' ? [1, 1.5] : breath === 'exhale' ? [1.5, 1] : 1,
          opacity: isVisible ? 0.2 : 0,
        }}
        transition={{
          duration: breath ? 5 : 0.5,
          ease: 'easeInOut',
        }}
        className="w-56 h-56 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(74,103,65,0.5) 0%, transparent 70%)',
        }}
      />
    </motion.div>
  );
};

// Closing Anchor Visual
const ClosingAnchor = ({ isVisible }) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute top-1/4 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
            }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
            className="w-16 h-16 rounded-full bg-emerald-600/20 flex items-center justify-center border border-emerald-500/30"
          >
            <Heart size={28} className="text-emerald-400" fill="rgba(52,211,153,0.3)" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Simple Progress Ring
const ProgressRing = ({ progress }) => {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20">
      <svg width="100" height="100" className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="2"
        />
        {/* Progress circle */}
        <motion.circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="rgba(74,103,65,0.6)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circumference}
          animate={{ strokeDashoffset }}
          transition={{ duration: 0.3, ease: 'linear' }}
        />
      </svg>
    </div>
  );
};

// Main Component
export const MiddayInterrupt = () => {
  const navigate = useNavigate();
  
  const [isStarted, setIsStarted] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  
  const startTimeRef = useRef(null);
  const content = MIDDAY_CONTENT;

  // Start after brief pause
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsStarted(true);
      startTimeRef.current = Date.now();
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Main timer
  useEffect(() => {
    if (!isStarted || isComplete) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setElapsedTime(elapsed);
      
      // Update progress
      const newProgress = Math.min((elapsed / content.totalDuration) * 100, 100);
      setProgress(newProgress);

      // Check for phase transitions
      const currentPhase = content.phases.findIndex((phase, index) => {
        const nextPhase = content.phases[index + 1];
        if (!nextPhase) return elapsed >= phase.startTime;
        return elapsed >= phase.startTime && elapsed < nextPhase.startTime;
      });
      
      if (currentPhase !== -1 && currentPhase !== currentPhaseIndex) {
        setCurrentPhaseIndex(currentPhase);
      }

      // Check completion
      if (elapsed >= content.totalDuration && !isComplete) {
        setIsComplete(true);
        clearInterval(interval);
        recordCompletion();
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isStarted, currentPhaseIndex, isComplete, content]);

  // Record completion
  const recordCompletion = async () => {
    const token = localStorage.getItem('reclaim_token');
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/presence-journey/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          day: 1, // Midday is always associated with current day
          session: 'midday'
        })
      });
    } catch (error) {
      console.error('Failed to record completion:', error);
    }
  };

  const handleClose = useCallback(() => {
    navigate(-1); // Go back to where they came from
  }, [navigate]);

  const currentPhase = content.phases[currentPhaseIndex];
  const showText = isStarted && currentPhase?.text;
  const showVoice = isStarted && currentPhase?.voiceText;
  const isAnchorPhase = currentPhase?.isAnchor;

  // Auto-close after completion
  useEffect(() => {
    if (isComplete) {
      const timer = setTimeout(() => {
        navigate(-1);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isComplete, navigate]);

  return (
    <div className="fixed inset-0 bg-[#0A0A09] overflow-hidden z-[90]">
      {/* Subtle radial gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(74,103,65,0.1) 0%, transparent 60%)'
        }}
      />

      {/* Noise texture */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Closing Anchor Visual */}
      <ClosingAnchor isVisible={isAnchorPhase && isStarted} />

      {/* Breath Circle */}
      <BreathCircle 
        breath={currentPhase?.breath} 
        isVisible={isStarted && !isComplete && !isAnchorPhase}
      />

      {/* Main Text */}
      <TextOverlay 
        text={currentPhase?.text} 
        isVisible={showText}
        isAnchor={isAnchorPhase}
      />

      {/* Voice Text */}
      <VoiceText 
        text={currentPhase?.voiceText} 
        isVisible={showVoice}
      />

      {/* Close Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        whileHover={{ opacity: 1 }}
        onClick={handleClose}
        className="absolute top-6 left-6 z-30 p-2 rounded-full hover:bg-white/10 transition-colors duration-300"
        data-testid="midday-close"
      >
        <X size={24} className="text-white" />
      </motion.button>

      {/* Progress Ring */}
      {isStarted && !isComplete && (
        <ProgressRing progress={progress} />
      )}

      {/* Completion Message */}
      <AnimatePresence>
        {isComplete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 flex items-center justify-center z-50"
          >
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-600/20 flex items-center justify-center border border-emerald-500/30"
              >
                <Heart size={24} className="text-emerald-400" fill="rgba(52,211,153,0.3)" />
              </motion.div>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-emerald-400/60 text-sm tracking-widest mb-3"
              >
                RESET COMPLETE
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="text-white/80 text-lg font-light"
              >
                Return with presence
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MiddayInterrupt;
